import { FC } from 'react';

import {
  useGiftCsMemoCache,
  useGiftCsMemoListQuery,
} from '@shared/api/giftCsMemo/giftCsMemo.hook';
import {
  GiftCsMemoTarget,
  resolveCsMemoTarget,
} from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { CsMemoAdapter, CsMemoButtonBase, CsMemoRecord } from '@shared/components/csMemo';
import { LowerRole } from '@shared/types';

/**
 * 배송관리자·공급사용 CS 응대 이력 보기 — **읽기 전용**.
 *
 * 왜 별도 어댑터인가:
 *  - 조회 경로가 다르다. Admin은 `/admin/gift-cs-memo`, 배송관리자는
 *    `/gift-brand-seller/gift-cs-memo`(자기 브랜드 배송 건으로 스코핑 + adminOnly 제외).
 *    배송관리자가 admin 경로를 두드리면 401이고, 그러면 전역 인터셉터가 알림 후
 *    화면을 되돌린다(로그아웃은 아니지만 작업이 끊긴다). 그래서 경로를 반드시 갈라야 한다.
 *  - 작성·삭제는 주지 않는다. 응대 주체는 마리·관리자이고 공급사는 참고만 한다.
 *
 * 백엔드 라우트가 아직 없으면 404 → `data === null` → 버튼이 '서버 미반영'으로 뜬다.
 * 스펙: docs/gift-cs-memo-brand-read-spec.md
 */
const useBrandMemoList = (target: GiftCsMemoTarget, enabled: boolean) => {
  const { data, isLoading } = useGiftCsMemoListQuery(
    target,
    enabled,
    LowerRole.GiftBrandSeller,
  );
  return { data, isLoading };
};

const useBrandMemoCache = (target: GiftCsMemoTarget) =>
  useGiftCsMemoCache(target, LowerRole.GiftBrandSeller);

// 읽기 전용이라 실제로 호출되지 않는다. readOnly가 입력·삭제 UI를 통째로 감춘다.
const useNoopMutation = () => ({
  mutateAsync: async () => {
    throw new Error('배송관리자는 CS 메모를 작성할 수 없습니다.');
  },
  isLoading: false,
});

export const brandCsMemoAdapter: CsMemoAdapter<GiftCsMemoTarget> = {
  useList: useBrandMemoList,
  useCache: useBrandMemoCache,
  useCreate: useNoopMutation,
  useDelete: useNoopMutation,
  isDeleteUnsupported: () => true,
  roleBadge: (byRole) =>
    byRole === 'Admin'
      ? { label: '관리자', className: 'bg-indigo-100 text-indigo-700' }
      : { label: '마리', className: 'bg-teal-100 text-teal-700' },
  hasTarget: (target) => !!resolveCsMemoTarget(target),
};

const EMPTY_TARGET: GiftCsMemoTarget = {};

interface Props {
  /** 이 배송 건에 달린 응대 이력 */
  deliveryId: string;
  subtitle?: string;
  memos?: CsMemoRecord[];
  variant?: 'cell' | 'button';
}

export const BrandCsMemoButton: FC<Props> = function BrandCsMemoButton({
  deliveryId,
  subtitle,
  memos,
  variant = 'cell',
}) {
  return (
    <CsMemoButtonBase
      adapter={brandCsMemoAdapter}
      target={{ giftDelivery: deliveryId }}
      emptyTarget={EMPTY_TARGET}
      title="CS 응대 이력"
      subtitle={subtitle}
      memos={memos}
      variant={variant}
      label="응대이력"
      readOnly
      notice="관리자·마리가 남긴 응대 기록입니다. 보기 전용이라 여기서는 작성·삭제할 수 없습니다."
    />
  );
};
